import { CardMedia } from '@mui/material';
import { OpenInNew, PlayCircleFilledTwoTone as PlayButton, RemoveCircleOutline } from '@mui/icons-material';
import { PostAccount } from 'components/PostAccount';
import { PostCaption } from 'components/PostCaption';
import { Post } from 'lib/types';
import { NETWORKS, POST_TYPES } from 'lib/constants';
import { createAltTextForPosts, userIsPaidTeamMember } from 'lib/helpers';
import { imagePostUrl } from 'lib/utils/imageUtils';
import { Emoji } from 'elements/Emoji';
import { MobileTooltip } from 'components/MobileTooltip';
import { useCurrentUser } from 'lib/contexts/ApplicationState';

interface Props {
  post: Post;
}

const shouldShowPlayButton = (type: number) => (
  (type === POST_TYPES.VIDEO || type === POST_TYPES.GIF)
  && <PlayButton fontSize="large" className="absolute bottom-1 left-1 text-white" />
);

const hasBeenRemoved = (network: string) => (
  <div>
    <MobileTooltip title={`This media has been removed from ${network}.`}>
      <RemoveCircleOutline fontSize="large" className="absolute top-1 left-1 text-white" />
    </MobileTooltip>
  </div>
);

export const EmbedFallback = ({ post }: Props) => {
  const currentUser = useCurrentUser();
  const imgBgUrl = post.thumbnailUrl ? imagePostUrl(post.thumbnailUrl, { isDead: !post.isLive || (post.type === POST_TYPES.STORY && !userIsPaidTeamMember(currentUser)) }) : '';
  const postNetwork = NETWORKS[post.network];

  const renderHeader = (
    <div className="p-2">
      <PostAccount
        size="medium"
        post={post}
        linkToProfile
      />
    </div>
  );

  const renderCaption = (
    <PostCaption networkId={post.network} className="p-4 border-t h-full">
      {post.caption}
    </PostCaption>
  );

  const renderImageOrCaption = (
    imgBgUrl
      ? (
        <div>
          <CardMedia
            component="img"
            alt={createAltTextForPosts(post)}
            image={imgBgUrl}
          />
          {shouldShowPlayButton(post.type)}
          {!post.isLive && hasBeenRemoved(postNetwork.title)}
        </div>
      )
      : renderCaption
  );

  const renderBody = (
    <div className="relative bg-hintgray dark:bg-darkgray h-full">
      {post.isLive
        ? <a href={post.url} target="_blank" rel="noreferrer">{renderImageOrCaption}</a>
        : renderImageOrCaption}
    </div>
  );

  const renderFooter = (
    <div className="absolute bg-white dark:bg-appbg-dark p-2 w-full bottom-0 text-center border-t text-sm">
      {post.isLive && post.type !== POST_TYPES.STORY ? (
        <a href={post.url} target="_blank" rel="noreferrer">
          View on
          {' '}
          {postNetwork.title}
          {' '}
          <OpenInNew fontSize="small" />
        </a>
      )
        : (
          <div>
            The original post
            {' '}
            {post.type === POST_TYPES.STORY ? 'is a story on' : 'has been removed from'}
            {' '}
            {postNetwork.title}
            {' '}
            and is no longer available. We&apos;ll keep the last data from it for posterity
            {' '}
            <Emoji emoji="🙂" description="smilie emoji" />
          </div>
        )}

    </div>
  );

  return (
    <div className="relative border rounded-lg shadow mb-6 w-11/12 h-full overflow-hidden mx-auto">
      {renderHeader}
      {renderBody}
      {renderFooter}
    </div>
  );
};
